<!-- =========================================================================================
  File Name: WizardStepSelection.vue
  Description: Step 2 - Select points using multiple methods (table, polygons, click, all)
  ----------------------------------------------------------------------------------------
  Author: Serviroutes Team
  Version: 2025-10-30
========================================================================================== -->

<template>
	<div class="wizard-step-selection">
		<div v-if="!isSelectionValid" class="text-center mb-2" style="color: #ff9800">
			⚠️ {{ $t("Wizard.Steps.Selection.MinimumRequiredPoint", {min: minimumPoints}) }}
		</div>
		<div class="step-content">
			<!-- Selection Methods Tabs -->
			<div class="selection-methods">
				<div class="methods-tabs">
					<div
						v-if="wizardData.loadMethod === 'skip'"
						class="method-tab"
						:class="{active: activeMethod === 'table'}"
						@click="activeMethod = 'table'"
					>
						<feather-icon icon="ListIcon" svgClasses="w-5 h-5" />
						<span>{{ $t("Wizard.Steps.Selection.FromTable") }}</span>
					</div>
					<div class="method-tab" :class="{active: activeMethod === 'map'}" @click="activeMethod = 'map'">
						<feather-icon icon="MapIcon" svgClasses="w-5 h-5" />
						<span>{{ $t("Wizard.Steps.Selection.FromMap") }}</span>
					</div>
					<div
						class="method-tab"
						:class="{active: activeMethod === 'create'}"
						@click="activeMethod = 'create'"
					>
						<feather-icon icon="PlusCircleIcon" svgClasses="w-5 h-5" />
						<span>{{ $t("Wizard.Steps.Selection.CreatePoint") }}</span>
					</div>
				</div>

				<!-- Method Content -->
				<div class="method-content">
					<!-- TABLE METHOD -->
					<div v-if="activeMethod === 'table' && wizardData.loadMethod === 'skip'" class="method-panel">
						<div class="panel-header">
							<h3>{{ $t("Wizard.Steps.Selection.SelectFromTable") }}</h3>
							<p>{{ $t("Wizard.Steps.Selection.TableHelp") }}</p>
						</div>

						<!-- Loading State -->
						<div v-if="isLoadingTable" class="table-loading">
							<vs-progress indeterminate color="primary" />
							<p>{{ $t("Loading") }} {{ $t("Clustering.LoadedPoints") }}...</p>
						</div>

						<!-- Search and Filters -->
						<div v-else class="table-controls">
							<vs-input
								v-model="tableSearch"
								:placeholder="$t('Wizard.SearchPoints')"
								icon-pack="feather"
								icon="icon-search"
								class="search-input"
							/>
							<div class="table-actions">
								<vs-button
									color="primary"
									type="border"
									size="small"
									icon-pack="feather"
									icon="icon-check-square"
									@click="selectAllFiltered"
								>
									{{ tableSearch.length > 0 ? $t("Wizard.SelectFiltered") : $t("Wizard.SelectAll") }}
								</vs-button>
								<vs-button
									color="warning"
									type="border"
									size="small"
									icon-pack="feather"
									icon="icon-x-square"
									:disabled="wizardData.selectedPoints.length === 0"
									@click="clearAllSelection"
								>
									{{ $t("Wizard.ClearSelection") }}
								</vs-button>
							</div>
						</div>

						<!-- Points Table -->
						<div class="points-table-wrapper">
							<table class="points-table">
								<caption></caption>
								<thead>
									<tr>
										<th class="checkbox-col">
											<vs-checkbox v-model="selectAllChecked" @change="toggleSelectAll" />
										</th>
										<th>#</th>
										<th>{{ $t("Routing.Table.Identification") }}</th>
										<th>{{ $t("Address") }}</th>
										<th>{{ $t("Clustering.Latitude") }}</th>
										<th>{{ $t("Clustering.Longitude") }}</th>
										<th>{{ $t("Clustering.Demand") }}</th>
										<th>{{ $t("Routing.Table.Actions") }}</th>
									</tr>
								</thead>
								<tbody>
									<tr
										v-for="(point, index) in paginatedTablePoints"
										:key="point.id"
										:class="{selected: isPointSelected(point.id)}"
									>
										<td class="checkbox-col">
											<vs-checkbox
												:checked="isPointSelected(point.id)"
												@change="togglePointSelection(point)"
											/>
										</td>
										<td>{{ index + 1 + (currentTablePage - 1) * tableItemsPerPage }}</td>
										<td>
											<strong>{{ point.identification || "-" }}</strong>
										</td>
										<td>{{ truncate(point.address, 40) }}</td>
										<td>{{ formatCoord(point.latitude) }}</td>
										<td>{{ formatCoord(point.longitude) }}</td>
										<td>
											<span class="demand-badge">{{ point.demand || 0 }}</span>
										</td>
										<td>
											<vs-button
												color="primary"
												type="flat"
												icon-pack="feather"
												icon="icon-map-pin"
												size="small"
												@click="zoomToPoint(point)"
											></vs-button>
										</td>
									</tr>
								</tbody>
							</table>
						</div>

						<!-- Pagination -->
						<div class="table-pagination">
							<vs-pagination v-model="currentTablePage" :total="totalTablePages" :max="7"></vs-pagination>
							<small class="pagination-info">
								{{
									$t("Wizard.ShowingItems", {
										from: (currentTablePage - 1) * tableItemsPerPage + 1,
										to: Math.min(currentTablePage * tableItemsPerPage, filteredTablePoints.length),
										total: filteredTablePoints.length,
									})
								}}
							</small>
						</div>
					</div>
					<!-- MAP METHOD -->
					<div v-if="activeMethod === 'map'" class="method-panel">
						<div class="panel-header">
							<h3>{{ $t("Wizard.Steps.Selection.SelectFromMap") }}</h3>
							<p>{{ $t("Wizard.Steps.Selection.MapHelp") }}</p>
						</div>
						<div class="step-header">
							<!-- Selection Status Bar -->
							<div class="selection-status-bar">
								<div class="status-item available">
									<feather-icon icon="CircleIcon" svgClasses="w-5 h-5" />
									<div class="status-content">
										<span class="status-value">{{ this.allAvailablePoints.length }}</span>
										<span class="status-label">{{ $t("Wizard.Available") }}</span>
									</div>
								</div>
								<div class="status-arrow">
									<feather-icon icon="ArrowRightIcon" svgClasses="w-6 h-6" />
								</div>
								<div class="status-item selected" :class="{valid: isSelectionValid}">
									<feather-icon icon="CheckCircleIcon" svgClasses="w-5 h-5" />
									<div class="status-content">
										<span class="status-value">{{ wizardData.selectedPoints.length }}</span>
										<span class="status-label">{{ $t("Wizard.Selected") }}</span>
									</div>
								</div>
							</div>
						</div>
						<div
							class="map-methods-grid"
							:style="{
								'grid-template-columns': wizardData.loadMethod === 'all' ? 'repeat(2, 1fr)' : '1fr',
							}"
						>
							<!-- Polygon Drawing -->
							<div v-if="wizardData.loadMethod === 'all'" class="map-method-card">
								<div class="method-card-icon">
									<feather-icon icon="Edit3Icon" svgClasses="w-8 h-8" />
								</div>
								<h4>{{ $t("Wizard.Steps.Selection.DrawPolygons") }}</h4>
								<p>{{ $t("Wizard.Steps.Selection.PolygonHelp") }}</p>
								<div class="drawing-controls">
									<vs-button
										v-if="!isDrawing"
										color="primary"
										type="gradient"
										icon-pack="feather"
										icon="icon-edit-3"
										class="w-full"
										@click="startPolygonDrawing"
									>
										{{ $t("Wizard.StartDrawing") }}
									</vs-button>
									<vs-button
										v-else
										color="danger"
										type="gradient"
										icon-pack="feather"
										icon="icon-x"
										class="w-full"
										@click="finishDrawing"
									>
										{{ $t("Cancel") }}
									</vs-button>
									<vs-button
										color="warning"
										icon-pack="feather"
										icon="icon-corner-up-left"
										:disabled="wizardData.drawnPolygons.length === 0"
										@click="undoLastPolygon"
									>
										{{ $t("Wizard.UndoLast") }}
									</vs-button>
									<vs-button
										color="danger"
										icon-pack="feather"
										icon="icon-trash-2"
										:disabled="wizardData.drawnPolygons.length === 0"
										@click="confirmClearAllPolygons"
									>
										{{ $t("Wizard.ClearAll") }}
									</vs-button>
								</div>
								<div v-if="wizardData.drawnPolygons.length > 0" class="polygon-stats">
									<small>
										<feather-icon icon="Edit3Icon" svgClasses="w-3 h-3" />
										{{ wizardData.drawnPolygons.length }} {{ $t("Wizard.PolygonsDrawn") }}
									</small>
								</div>
							</div>

							<!-- Create Point on Map Click -->
							<div class="map-method-card">
								<div class="method-card-icon">
									<feather-icon icon="MapPinIcon" svgClasses="w-8 h-8" />
								</div>
								<h4>{{ $t("Wizard.Steps.Selection.CreateByClick") }}</h4>
								<p>{{ $t("Wizard.Steps.Selection.CreateByClickHelp") }}</p>
								<vs-button
									v-if="!isCreatingByClick"
									color="success"
									icon-pack="feather"
									icon="icon-map-pin"
									class="w-full"
									@click="startCreatingByClick"
								>
									{{ $t("Wizard.Steps.Selection.StartClickCreate") }}
								</vs-button>
								<div v-else class="drawing-controls">
									<vs-button
										color="danger"
										type="border"
										size="small"
										icon-pack="feather"
										icon="icon-x"
										@click="stopCreatingByClick"
									>
										{{ $t("Wizard.Steps.Selection.StopClickCreate") }}
									</vs-button>
								</div>
								<div v-if="clickCreatedPoints > 0" class="polygon-stats">
									<small
										>{{ clickCreatedPoints }}
										{{ $t("Wizard.Steps.Selection.PointsCreatedByClick") }}</small
									>
								</div>
							</div>
							<vs-prompt
								:title="$t('Wizard.Steps.Selection.EnterIdentification')"
								:active.sync="activeAddressPrompt"
								@cancel="close"
								:is-valid="isDataGeoValid"
								:acceptText="$t('Confirm')"
								:cancelText="$t('Cancel')"
								@accept="acceptAlert"
								@close="close"
							>
								<div style="margin: 20px 0">
									<label style="display: block; margin-bottom: 8px; font-weight: 600">
										{{ $t("Identification") }}:
									</label>
									<vs-input
										v-model="dataGeo.identification"
										type="text"
										:id="`input-identification-${Date.now()}`"
										:value="`Punto ${clickCreatedPoints + 1}`"
										:placeholder="$t('Identification')"
										class="mt-3 w-full mb-4"
									/>
								</div>
								<div class="con-exemple-prompt">
									<p>
										{{ $t("Wizard.Steps.Selection.AddressFound") }}
									</p>
									<vs-input
										:placeholder="$t('Address')"
										v-model="dataGeo.address"
										class="mt-3 w-full mb-4"
									/>
									<p>{{ $t("Deposits.Form.GeoRef.UseVisitPoint") }}</p>
								</div>
							</vs-prompt>
						</div>
					</div>
					<!-- CREATE POINT METHOD -->
					<div v-if="activeMethod === 'create'" class="method-panel">
						<div class="panel-header">
							<h3>{{ $t("Wizard.Steps.Selection.CreateNewPoint") }}</h3>
							<p>{{ $t("Wizard.Steps.Selection.CreateHelp") }}</p>
						</div>

						<div class="create-point-form">
							<div class="form-section">
								<h4>{{ $t("Clustering.PointDetails") }}</h4>

								<div class="form-row">
									<vs-input
										v-model="newPoint.identification"
										:label="$t('Routing.Table.Identification')"
										placeholder="Ej: VP-001"
										class="w-full"
									/>
								</div>

								<div class="form-row-grid">
									<vs-input
										v-model="newPoint.latitude"
										:label="$t('Clustering.Latitude')"
										placeholder="Ej: 4.7110"
										type="number"
										step="0.000001"
										class="w-full"
									/>
									<vs-input
										v-model="newPoint.longitude"
										:label="$t('Clustering.Longitude')"
										placeholder="Ej: -74.0721"
										type="number"
										step="0.000001"
										class="w-full"
									/>
								</div>

								<div class="form-row">
									<vs-input
										v-model="newPoint.demand"
										:label="$t('Clustering.Demand')"
										type="number"
										min="0"
										class="w-full"
									/>
								</div>

								<div class="form-actions">
									<vs-button
										color="dark"
										type="border"
										icon-pack="feather"
										icon="icon-refresh-cw"
										@click="resetNewPointForm"
									>
										{{ $t("ResourceModule.Create.Clear") }}
									</vs-button>
									<vs-button
										color="success"
										type="gradient"
										icon-pack="feather"
										icon="icon-plus-circle"
										:disabled="!isNewPointValid"
										@click="addCreatedPoint"
									>
										{{ $t("ResourceModule.Create.Add") }}
									</vs-button>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import moduleMap from "@/store/map/moduleMap.js";
export default {
	name: "WizardStepSelection",
	props: {
		wizardData: {
			type: Object,
			required: true,
		},
		map: {
			type: Object,
			default: null,
		},
	},
	data() {
		return {
			activeMethod: "table",
			minimumPoints: 1,
			activeAddressPrompt: false,
			address: "",
			dataGeo: {
				identification: "",
				latitude: "",
				longitude: "",
				address: "",
			},

			// Table
			tableSearch: "",
			currentTablePage: 1,
			tableItemsPerPage: 10,
			selectAllChecked: false,
			allAvailablePoints: [],
			isLoadingTable: false,

			// Create Point
			newPoint: {
				identification: "",
				latitude: "",
				longitude: "",
				demand: 0,
			},
			createdPoints: [],
			nextTempId: 1,

			// Map
			isDrawing: false,
			isCreatingByClick: false,
			clickCreatedPoints: 0,

			// Summary
			showSelectedList: false,
		};
	},
	computed: {
		isDataGeoValid() {
			return this.dataGeo.identification !== "" && this.dataGeo.address !== "";
		},
		isSelectionValid() {
			return this.wizardData.selectedPoints.length >= this.minimumPoints;
		},
		filteredTablePoints() {
			// Use allAvailablePoints (from database) instead of loadedPoints (from map)
			const sourcePoints =
				this.allAvailablePoints.length > 0 ? this.allAvailablePoints : this.wizardData.loadedPoints;
			if (!this.tableSearch) {
				return sourcePoints;
			}
			const query = this.tableSearch.toLowerCase();
			return sourcePoints.filter((point) => {
				const id = (point.identification || "").toLowerCase();
				const address = (point.address || "").toLowerCase();
				return id.includes(query) || address.includes(query);
			});
		},
		paginatedTablePoints() {
			const start = (this.currentTablePage - 1) * this.tableItemsPerPage;
			const end = start + this.tableItemsPerPage;
			return this.filteredTablePoints.slice(start, end);
		},
		totalTablePages() {
			return Math.ceil(this.filteredTablePoints.length / this.tableItemsPerPage);
		},
		isNewPointValid() {
			return (
				this.newPoint.latitude !== "" &&
				this.newPoint.longitude !== "" &&
				!isNaN(parseFloat(this.newPoint.latitude)) &&
				!isNaN(parseFloat(this.newPoint.longitude))
			);
		},
	},
	watch: {
		// Watch for tab changes to clean up drawing modes
		activeMethod(newMethod, oldMethod) {
			// If switching away from map tab, stop any active drawing modes
			if (oldMethod === "map" && newMethod !== "map") {
				// Stop polygon drawing if active
				if (this.isDrawing) {
					this.isDrawing = false;
					this.$parent.$emit("finish-polygon-drawing");
				}
				// Stop click create mode if active
				if (this.isCreatingByClick) {
					this.stopCreatingByClick();
				}
			}
		},
	},
	mounted() {
		if (!moduleMap.isRegistered) {
			this.$store.registerModule("moduleMap", moduleMap);
			moduleMap.isRegistered = true;
		}
		// Determine initial active tab based on load method from Step 1
		if (this.wizardData.loadMethod === "all") {
			// If user loaded all points to map, show map selection methods
			this.activeMethod = "map";
		} else if (this.wizardData.loadMethod === "skip") {
			// If user skipped loading, show table to select from DB
			this.activeMethod = "table";
		} else {
			// Default to table
			this.activeMethod = "table";
		}

		// Load all available points when entering this step
		this.loadAllAvailablePoints();
	},
	methods: {
		/**
		 * Load all available visit points from database
		 */
		async loadAllAvailablePoints() {
			this.isLoadingTable = true;
			try {
				const response = await this.$store.dispatch("moduleVisitPoint/findVisitPointPaginated", {
					page: 1,
					order_by: ["id"],
					sort: "asc",
				});

				if (response && response.records && response.records.length > 0) {
					this.allAvailablePoints = response.records.map((vp) => ({
						id: vp.id,
						identification: vp.identification || null,
						address: vp.address || null,
						latitude: parseFloat(vp.cy),
						longitude: parseFloat(vp.cx),
						demand: parseInt(vp.demand) || 0,
					}));
				}
			} catch (error) {
				this.$vs.notify({
					color: "danger",
					title: this.$t("Error"),
					text: this.$t("Clustering.ErrorLoadingPoints"),
				});
			} finally {
				this.isLoadingTable = false;
			}
		},

		isPointSelected(pointId) {
			return this.wizardData.selectedPoints.some((p) => p.id === pointId);
		},
		togglePointSelection(point) {
			const selected = [...this.wizardData.selectedPoints];
			const index = selected.findIndex((p) => p.id === point.id);
			if (index > -1) {
				// Deseleccionar: remover de selectedPoints
				selected.splice(index, 1);
				//También remover de loadedPoints para que desaparezca del mapa
				const loadedPoints = this.wizardData.loadedPoints.filter((p) => p.id !== point.id);
				this.$emit("update-data", {
					selectedPoints: selected,
					loadedPoints: loadedPoints,
				});
				this.$parent.$emit("load-points-to-map", loadedPoints);
			} else {
				selected.push(point);

				// Also add to loadedPoints if not already there
				const loadedPoints = [...this.wizardData.loadedPoints];
				if (!loadedPoints.some((p) => p.id === point.id)) {
					loadedPoints.push(point);
					this.$emit("update-data", {
						selectedPoints: selected,
						loadedPoints: loadedPoints,
					});
					// Update map
					this.$parent.$emit("load-points-to-map", loadedPoints);
					return;
				}
			}

			this.$emit("update-data", {selectedPoints: selected});
		},
		toggleSelectAll() {
			if (this.selectAllChecked) {
				this.selectAllPoints();
			} else {
				this.clearAllSelection();
			}
		},
		selectAllFiltered() {
			const selected = [...this.wizardData.selectedPoints];
			const loadedPoints = [...this.wizardData.loadedPoints];
			this.filteredTablePoints.forEach((point) => {
				if (!selected.some((p) => p.id === point.id)) {
					selected.push(point);
				}
				// Add to loaded points if not there
				if (!loadedPoints.some((p) => p.id === point.id)) {
					loadedPoints.push(point);
				}
			});

			this.$emit("update-data", {
				selectedPoints: selected,
				loadedPoints: loadedPoints,
			});

			// Update map
			this.$parent.$emit("load-points-to-map", loadedPoints);
		},
		selectAllPoints() {
			// Select all from filtered table points (all available)
			const allPoints = this.filteredTablePoints;

			this.$emit("update-data", {
				selectedPoints: [...allPoints],
				loadedPoints: [...allPoints],
			});

			// Update map
			this.$parent.$emit("load-points-to-map", allPoints);

			this.selectAllChecked = true;

			this.$vs.notify({
				color: "success",
				title: this.$t("Success"),
				text: this.$t("Wizard.Steps.Selection.AllSelected"),
			});
		},
		clearAllSelection() {
			this.$emit("update-data", {selectedPoints: [], loadedPoints: []});
			this.selectAllChecked = false;
			this.$parent.$emit("load-points-to-map", []);
		},
		removePoint(point) {
			const selected = this.wizardData.selectedPoints.filter((p) => p.id !== point.id);
			this.$emit("update-data", {selectedPoints: selected});
		},
		startPolygonDrawing() {
			this.isDrawing = true;
			// Emit to parent wizard, which will emit to ClusteringMap
			this.$parent.$emit("start-polygon-drawing");
		},
		finishDrawing() {
			this.isDrawing = false;
			this.$parent.$emit("finish-polygon-drawing");
		},
		stopDrawingMode() {
			this.isDrawing = false;
		},
		undoLastPolygon() {
			if (this.wizardData.drawnPolygons.length === 0) return;

			this.$parent.$emit("undo-last-polygon");

			this.$vs.notify({
				color: "warning",
				title: this.$t("Wizard.PolygonRemoved"),
				text: this.$t("Wizard.LastPolygonRemoved"),
			});
		},
		confirmClearAllPolygons() {
			this.$vs.dialog({
				type: "confirm",
				color: "danger",
				title: this.$t("Confirm"),
				text: this.$t("Wizard.ConfirmClearAllPolygons"),
				acceptText: this.$t("Crud.Delete"),
				cancelText: this.$t("Cancel"),
				accept: () => {
					this.clearAllPolygons();
				},
			});
		},
		clearAllPolygons() {
			this.$parent.$emit("clear-all-polygons");

			this.$vs.notify({
				color: "danger",
				title: this.$t("Wizard.AllPolygonsCleared"),
				text: this.$t("Wizard.PointsDeselected"),
			});
		},
		zoomToPoint(point) {
			this.$emit("zoom-to-point", point);
		},
		startCreatingByClick() {
			this.isCreatingByClick = true;
			// Emit to parent wizard, which will emit to ClusteringMap
			this.$parent.$emit("start-click-create");
		},
		stopCreatingByClick() {
			this.isCreatingByClick = false;
			this.$parent.$emit("stop-click-create");
		},
		async handleMapClick(event) {
			const {lat, lng} = event;
			// Show loading notification
			this.$vs.loading();
			try {
				this.$store
					.dispatch("moduleMap/geoinverso", {cy: lat, cx: lng})
					.then((res) => {
						this.$vs.loading.close();
						this.dataGeo = {
							latitude: parseFloat(lat.toFixed(8)),
							longitude: parseFloat(lng.toFixed(8)),
							address: "",
							identification: `Punto ${this.clickCreatedPoints + 1}`,
						};
						if (res && res.data && res.data.address1) {
							this.dataGeo.address = res.data.address1;
							this.dataGeo.latitude = parseFloat(lat.toFixed(8));
							this.dataGeo.longitude = parseFloat(lng.toFixed(8));
							this.activeAddressPrompt = true;

							// Stop creation mode automatically for better UX
							this.stopCreatingByClick();
						}
					})
					.catch((err) => {
						this.$vs.loading.close();
						this.submitButtonDisabled = true;
						// Stop creation mode even on error
						this.stopCreatingByClick();
					});
			} catch (error) {
				this.$vs.loading.close();
				// Show prompt anyway with default address
				this.dataGeo.address = defaultAddress;
				this.dataGeo.latitude = parseFloat(lat.toFixed(8));
				this.dataGeo.longitude = parseFloat(lng.toFixed(8));
				this.activeAddressPrompt = true;

				// Stop creation mode automatically for better UX
				this.stopCreatingByClick();
			}
		},
		showCreatePointPrompt(lat, lng, address) {
			// Create identification input value
			let identificationValue = `Punto ${this.clickCreatedPoints + 1}`;
			// Use vs-dialog instead of vs-prompt (which doesn't exist)
			this.$vs.dialog({
				type: "confirm",
				color: "primary",
				title: this.$t("Wizard.Steps.Selection.EnterIdentification"),
				text: `${this.$t("Wizard.Steps.Selection.AddressFound")}: ${address}`,
				accept: () => {
					// Show input prompt for identification
					const inputId = `input-identification-${Date.now()}`;
					this.$vs.dialog({
						type: "confirm",
						color: "success",
						title: this.$t("Wizard.Steps.Selection.EnterIdentification"),
						text: `
							<div style="margin: 20px 0;">
								<label style="display: block; margin-bottom: 8px; font-weight: 600;">
									${this.$t("Identification")}:
								</label>
								<input
									id="${inputId}"
									type="text"
									value="${identificationValue}"
									placeholder="${this.$t("Identification")}"
									style="width: 100%; padding: 10px; border: 1px solid #ccc; border-radius: 5px; font-size: 14px;"
								/>
							</div>
						`,
						acceptText: this.$t("Confirm"),
						cancelText: this.$t("Cancel"),
						accept: () => {
							const inputElement = document.getElementById(inputId);
							let identification = inputElement ? inputElement.value : identificationValue;
							if (!identification || identification.trim() === "") {
								identification = identificationValue;
							}
							this.createPointFromMapClick(lat, lng, address, identification);
						},
					});
				},
				cancel: () => {
					// User cancelled
					this.$vs.notify({
						color: "warning",
						title: this.$t("Information"),
						text: this.$t("Wizard.Steps.Selection.CreateCancelled"),
					});
				},
			});
		},
		createPointFromMapClick(lat, lng, address, identification) {
			if (!identification) {
				identification = `Punto ${this.clickCreatedPoints + 1}`;
			}
			const newPoint = {
				tempId: `click_${this.nextTempId++}`,
				id: `click_${Date.now()}`,
				identification: identification,
				address: address,
				latitude: lat,
				longitude: lng,
				demand: 0,
				isCreatedManually: true,
				createdByClick: true,
			};

			// Add to created points list
			this.createdPoints.push(newPoint);
			this.clickCreatedPoints++;

			// Add to loaded points
			const loadedPoints = [...this.wizardData.loadedPoints];
			loadedPoints.push(newPoint);

			// Add to selected points
			const selectedPoints = [...this.wizardData.selectedPoints];
			selectedPoints.push(newPoint);

			this.$emit("update-data", {
				loadedPoints: loadedPoints,
				selectedPoints: selectedPoints,
			});

			// Emit to parent to render on map
			this.$parent.$emit("load-points-to-map", loadedPoints);

			this.$vs.notify({
				color: "success",
				title: this.$t("Success"),
				text: this.$t("Wizard.Steps.Selection.PointCreatedFromMap", {identification}),
			});
		},
		truncate(text, length) {
			if (!text) return "-";
			return text.length > length ? text.substring(0, length) + "..." : text;
		},
		formatCoord(value) {
			return value ? parseFloat(value).toFixed(6) : "-";
		},
		addCreatedPoint() {
			if (!this.isNewPointValid) return;
			const newPoint = {
				tempId: `temp_${this.nextTempId++}`,
				id: `temp_${Date.now()}`,
				identification: this.newPoint.identification || `Punto ${this.createdPoints.length + 1}`,
				latitude: parseFloat(this.newPoint.latitude),
				longitude: parseFloat(this.newPoint.longitude),
				cx: parseFloat(this.newPoint.longitude),
				cy: parseFloat(this.newPoint.latitude),
				demand: parseInt(this.newPoint.demand) || 0,
				isCreatedManually: true,
			};

			// Add to created points list
			this.createdPoints.push(newPoint);

			// Add to loaded points (if not already there)
			const loadedPoints = [...this.wizardData.loadedPoints];
			loadedPoints.push(newPoint);

			// Add to selected points
			const selectedPoints = [...this.wizardData.selectedPoints];
			selectedPoints.push(newPoint);

			this.$emit("update-data", {
				loadedPoints: loadedPoints,
				selectedPoints: selectedPoints,
			});

			// Emit to parent to render on map
			this.$parent.$emit("load-points-to-map", loadedPoints);

			this.$vs.notify({
				color: "success",
				title: this.$t("Success"),
				text: this.$t("Wizard.Steps.Selection.PointCreated"),
			});

			// Reset form
			this.resetNewPointForm();
		},
		resetNewPointForm() {
			this.newPoint = {
				identification: "",
				latitude: "",
				longitude: "",
				demand: 0,
			};
		},
		removeCreatedPoint(point) {
			// Remove from created points
			this.createdPoints = this.createdPoints.filter((p) => p.tempId !== point.tempId);

			// Remove from loaded points
			const loadedPoints = this.wizardData.loadedPoints.filter((p) => p.id !== point.id);

			// Remove from selected points
			const selectedPoints = this.wizardData.selectedPoints.filter((p) => p.id !== point.id);

			this.$emit("update-data", {
				loadedPoints: loadedPoints,
				selectedPoints: selectedPoints,
			});

			// Update map
			this.$parent.$emit("load-points-to-map", loadedPoints);

			this.$vs.notify({
				color: "warning",
				title: this.$t("Warning"),
				text: this.$t("Wizard.Steps.Selection.PointRemoved"),
			});
		},
		acceptAlert() {
			this.createPointFromMapClick(
				this.dataGeo.latitude,
				this.dataGeo.longitude,
				this.dataGeo.address,
				this.dataGeo.identification
			);
		},
		close() {
			this.activeAddressPrompt = false;
			this.dataGeo = {
				identification: "",
				latitude: "",
				longitude: "",
				address: "",
			};
		},
	},
	beforeDestroy() {
		// Stop click create mode
		this.stopCreatingByClick();
		// Stop polygon drawing mode
		if (this.isDrawing) {
			this.isDrawing = false;
			this.$parent.$emit("finish-polygon-drawing");
		}
	},
};
</script>

<style lang="scss" scoped>
.wizard-step-selection {
	.step-header {
		text-align: center;
		margin-bottom: 32px;

		.step-title {
			font-size: 1.5rem;
			font-weight: 700;
			color: #333;
			margin-bottom: 8px;
		}

		.step-description {
			font-size: 0.95rem;
			color: #666;
			margin-bottom: 16px;
		}

		.selection-status-bar {
			display: flex;
			align-items: center;
			justify-content: center;
			gap: 16px;
			padding: 20px;
			background: #f8f9fa;
			border-radius: 12px;
			margin: 0 auto;
			max-width: 600px;
			position: relative;

			.status-item {
				display: flex;
				align-items: center;
				gap: 12px;
				padding: 12px 20px;
				background: white;
				border-radius: 8px;
				border: 2px solid #e0e0e0;

				&.available {
					border-color: rgba(var(--vs-success), 1);
					color: rgba(var(--vs-success), 1);
				}

				&.selected {
					border-color: rgba(var(--vs-primary), 1);
					color: rgba(var(--vs-primary), 1);

					&.valid {
						border-color: #4caf50;
						color: #4caf50;
					}
				}

				.status-content {
					display: flex;
					flex-direction: column;

					.status-value {
						font-size: 1.5rem;
						font-weight: 700;
						line-height: 1;
					}

					.status-label {
						font-size: 0.8rem;
						opacity: 0.8;
					}
				}
			}

			.status-arrow {
				color: #999;
			}

			.status-warning {
				position: absolute;
				bottom: -30px;
				left: 50%;
				transform: translateX(-50%);
				color: #ff9800;
				font-size: 0.85rem;
				white-space: nowrap;
			}
		}
	}

	.step-content {
		.selection-methods {
			.methods-tabs {
				display: flex;
				gap: 8px;
				margin-bottom: 24px;
				border-bottom: 2px solid #e0e0e0;

				.method-tab {
					flex: 1;
					display: flex;
					flex-direction: column;
					align-items: center;
					justify-content: center;
					gap: 4px;
					padding: 12px 8px;
					background: #f8f9fa;
					border-radius: 8px 8px 0 0;
					cursor: pointer;
					transition: all 0.2s ease;
					position: relative;
					font-size: 0.85rem;

					&:hover {
						background: #e3f2fd;
					}

					&.active {
						background: white;
						color: var(--vs-primary);
						font-weight: 600;

						&::after {
							content: "";
							position: absolute;
							bottom: -2px;
							left: 0;
							right: 0;
							height: 2px;
							background: var(--vs-primary);
						}
					}
				}
			}

			.method-content {
				min-height: 400px;

				.method-panel {
					.panel-header {
						margin-bottom: 24px;

						h3 {
							font-size: 1.5rem;
							margin-bottom: 8px;
						}

						p {
							color: #666;
						}
					}

					// Table styles
					.table-loading {
						text-align: center;
						padding: 40px 20px;

						p {
							margin-top: 16px;
							color: #666;
						}
					}

					.table-controls {
						display: flex;
						gap: 16px;
						margin-bottom: 16px;

						.search-input {
							flex: 1;
						}

						.table-actions {
							display: flex;
							gap: 8px;
						}
					}

					.points-table-wrapper {
						overflow-x: auto;
						border-radius: 8px;
						border: 1px solid #e0e0e0;

						.points-table {
							width: 100%;
							border-collapse: collapse;

							thead {
								background: #f5f5f5;

								th {
									padding: 12px;
									text-align: left;
									font-weight: 600;
									border-bottom: 2px solid #e0e0e0;

									&.checkbox-col {
										width: 40px;
										text-align: center;
									}
								}
							}

							tbody {
								tr {
									&:hover {
										background: #f9f9f9;
									}

									&.selected {
										background: rgba(76, 175, 80, 0.1);
									}

									td {
										padding: 12px;
										border-bottom: 1px solid #e0e0e0;

										&.checkbox-col {
											text-align: center;
										}

										.demand-badge {
											background: rgba(var(--vs-success), 1);
											color: white;
											padding: 4px 8px;
											border-radius: 4px;
											font-size: 0.85rem;
										}
									}
								}
							}
						}
					}

					.table-pagination {
						margin-top: 16px;
						display: flex;
						flex-direction: column;
						align-items: center;
						gap: 12px;

						.pagination-info {
							color: #666;
						}
					}

					// Map styles
					.map-methods-grid {
						display: grid;
						grid-template-columns: repeat(2, 1fr);
						gap: 24px;

						.map-method-card {
							background: #f8f9fa;
							padding: 24px;
							border-radius: 12px;
							text-align: center;

							.method-card-icon {
								color: var(--vs-primary);
								margin-bottom: 16px;
								display: flex;
								justify-content: center;
							}

							h4 {
								font-size: 1.25rem;
								margin-bottom: 12px;
							}

							p {
								color: #666;
								margin-bottom: 20px;
							}

							.drawing-controls {
								display: flex;
								flex-direction: column;
								gap: 8px;
								justify-content: center;
								margin-top: 12px;
							}

							.polygon-stats {
								margin-top: 12px;
								padding: 8px 12px;
								background: rgba(33, 150, 243, 0.1);
								border-radius: 6px;
								border-left: 3px solid #420d91;

								small {
									color: #420d91;
									font-weight: 600;
									display: flex;
									align-items: center;
									gap: 6px;
									justify-content: center;
								}
							}

							.click-legend {
								display: flex;
								flex-direction: column;
								gap: 8px;

								.legend-item {
									display: flex;
									align-items: center;
									gap: 8px;
									justify-content: center;

									.legend-dot {
										width: 16px;
										height: 16px;
										border-radius: 50%;

										&.gray {
											background: #9e9e9e;
										}

										&.green {
											background: #4caf50;
										}
									}
								}
							}
						}
					}

					// Create Point styles
					.create-point-form {
						.form-section {
							background: #f8f9fa;
							padding: 24px;
							border-radius: 12px;
							margin-bottom: 24px;

							h4 {
								font-size: 1.25rem;
								margin-bottom: 20px;
								color: #333;
							}

							.form-row {
								margin-bottom: 20px;
							}

							.form-row-grid {
								display: grid;
								grid-template-columns: repeat(2, 1fr);
								gap: 16px;
								margin-bottom: 20px;
							}

							.form-actions {
								display: flex;
								justify-content: flex-end;
								align-items: center;
								gap: 12px;
								margin-top: 24px;
							}
						}
					}
				}
			}
		}
	}
}

.expand-enter-active,
.expand-leave-active {
	transition: all 0.3s ease;
	max-height: 600px;
}

.expand-enter,
.expand-leave-to {
	max-height: 0;
	opacity: 0;
}

@media screen and (max-width: 768px) {
	.wizard-step-selection {
		.map-methods-grid {
			grid-template-columns: 1fr !important;
		}
	}
}
</style>
